import React, { Fragment, useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Box,
  Drawer,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import classNames from "classnames";

import DetailsCard from "shared/components/DetailsCard";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import {
  getProductManagerSubscriptionsList,
  planPaymentsList,
  refundedPlanPaymentsList,
} from "shared/services/productManagerService";
import {
  getProjectHeadPlanPaymentsList,
  getProjectHeadSubscriptionsList,
} from "shared/services/projectHeadService";
import {
  TRANSACTION_STATUSES,
  bankCredentials,
  bankDetails,
} from "pages/subscriptionRequest/constants";
import { routeConstant } from "shared/routes/routesConstant";
import {
  columnHeadings,
  emptyPlanPaymentFilters,
  planPaymentColumnHeadings,
  planPaymentMethods,
  refundColumnHeadings,
  actions,
  headings,
  transactionTypes,
  skeletonInfo,
} from "./constants";
import useDebounce from "shared/customHooks/useDebounce";
import ViewTransactionListModal from "shared/modals/viewTransactionListModal";
import { toastMessage } from "shared/components/toast";
import { useSelector } from "react-redux";
import { getRoutes } from "shared/components/sidebar/constant";
import styles from "./style.module.scss";
import { Icons } from "assets";
import TableSkeleton from "shared/components/TableSkeleton";
import ItemsPerPage from "shared/components/itemsPerPage";
import NoDataFound from "shared/components/noDataFound";
import useWindowSize from "shared/customHooks/windowSize";
import Pagination from "shared/components/pagination";
import {
  plan_level_enums,
  plans_types_enums,
} from "shared/modals/managePlans/constant";
const paymentTabs = {
  all: "all",
  approved: "approved",
  inApp: "inapp",
  inAppRefunds: "inapp_refunds",
};
// Must match the order of the rendered tabs; the in-app tabs are hidden for now.
const paymentTabOrder = [
  paymentTabs.all,
  paymentTabs.approved,
  // paymentTabs.inApp,
  // paymentTabs.inAppRefunds,
];

const getCurrency = (plan: any) =>
  plan?.level === plan_level_enums.in_app_international ||
  plan?.level === plan_level_enums.international
    ? "$"
    : "Rs.";

const formatDate = (date: any) =>
  date ? moment(date).format("DD MMM YYYY") : "Not Provided";

const Subscriptions = () => {
  const { user } = useSelector((state: any) => state.root);
  const screenWidth = useWindowSize();
  // Starts true: getSubscriptionList() runs from a useEffect, which fires only
  // after the first render commits. Starting false paints "No Data Found" for
  // a frame (the list is still empty) before the skeleton ever appears.
  const [loader, setLoader] = useState<boolean>(true);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchText, setSearchText] = useState<string>("");
  const [totalItems, setTotalItems] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activePage, setActivePage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(1);
  const [search, setSearch] = useState<string>("");
  const [approvedSubscription, setApprovedSubscription] = useState<any>([]);
  const [viewUserTransactionList, setViewUserTransactionList] =
    useState<boolean>(false);

  const [subscriptionId, setSubscriptionId] = useState<number | null>(null);
  const [isSchool, setIsSchool] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(paymentTabs.all);
  // Approved Payments filters being edited in the drawer, and the copy the
  // request uses; the drawer's values only reach the API on Apply.
  const [planPaymentFilters, setPlanPaymentFilters] = useState(
    emptyPlanPaymentFilters,
  );
  const [appliedPlanPaymentFilters, setAppliedPlanPaymentFilters] = useState(
    emptyPlanPaymentFilters,
  );
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  // Id of the latest list request. Switching tabs quickly can make an older
  // request finish last, so only the latest one may update the table.
  const latestRequestId = useRef<number>(0);
  const routes = getRoutes();
  const activeRole = routes.find(
    (role: any) => role.role_id === Number(user?.user?.role),
  );

  const roles = {
    projectHead: 1,
    productManager: 4,
  };
  const isProductManager = activeRole?.role_id === roles.productManager;
  const isProjectHead = activeRole?.role_id === roles.projectHead;
  const isRefundsTab = activeTab === paymentTabs.inAppRefunds;
  const isApprovedTab = activeTab === paymentTabs.approved;
  // Plan payment tabs use their own columns and have no actions.
  const tableColumns = isRefundsTab
    ? refundColumnHeadings
    : isApprovedTab
      ? planPaymentColumnHeadings
      : columnHeadings;
  const hasActions = !isRefundsTab && !isApprovedTab;
  const getPlanPayments = isProductManager
    ? planPaymentsList
    : getProjectHeadPlanPaymentsList;

  const handleView = (item: any) => {
    setSubscriptionId(item?.id);
    setIsSchool(item?.user?.school ? true : false);
    setViewUserTransactionList(true);
  };

  const handlers = [{ id: "view", function: handleView }];

  useBreadcrumbs({
    title: "Subscriptions History",
    path: routeConstant.subscriptions.path,
  });

  const getSubscriptionList = () => {
    setLoader(true);
    const requestId = ++latestRequestId.current;
    const request =
      activeRole.role_id === roles.productManager && isRefundsTab
        ? refundedPlanPaymentsList(search, activePage, itemsPerPage)
        : isApprovedTab
          ? getPlanPayments(
              search,
              activePage,
              itemsPerPage,
              appliedPlanPaymentFilters,
            )
          : activeRole.role_id === roles.productManager
            ? getProductManagerSubscriptionsList(
                null,
                search,
                1,
                activePage,
                itemsPerPage,
                activeTab === paymentTabs.inApp ? paymentTabs.inApp : undefined,
              )
            : getProjectHeadSubscriptionsList(
                null,
                search,
                1,
                activePage,
                itemsPerPage,
              );

    request
      .then(({ data, status }) => {
        if (requestId !== latestRequestId.current) return;
        if (status) {
          setApprovedSubscription(data?.data?.data);
          setLoader(false);
          setTotalPage(data?.data?.last_page);
          setTotalItems(data?.data?.total);
          setCurrentPage(data?.data?.current_page);
          setItemsPerPage(data?.data?.per_page);
        }
      })
      .catch((err: any) => {
        if (requestId !== latestRequestId.current) return;
        console.log("Error---", err);
        // Clear the table so a failed tab doesn't keep the previous tab's rows.
        setApprovedSubscription([]);
        setLoader(false);
        toastMessage("error", err?.response?.data?.message);
      });
  };

  useDebounce(
    () => {
      const trimmedSearch = search.trim();
      if (trimmedSearch === searchText) return;
      setSearchText(trimmedSearch);
      setActivePage(1);
    },
    [search],
    800,
  );

  useEffect(() => {
    getSubscriptionList();
  }, [
    searchText,
    itemsPerPage,
    activePage,
    activeTab,
    appliedPlanPaymentFilters,
  ]);

  const today = moment().format("YYYY-MM-DD");

  const handlePlanPaymentFilter = (key: string, value: string) => {
    // A typed date can get past the picker's min/max, so check it here too:
    // no future dates and To not before From (the same day is fine).
    if (key === "date_from" || key === "date_to") {
      const from = key === "date_from" ? value : planPaymentFilters.date_from;
      const to = key === "date_to" ? value : planPaymentFilters.date_to;
      if (value > today || (from && to && from > to)) return;
    }
    setPlanPaymentFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Opening starts from the applied filters, so edits left without Apply
  // are dropped when the drawer is closed.
  const openPlanPaymentFilters = () => {
    setPlanPaymentFilters(appliedPlanPaymentFilters);
    setFiltersOpen(true);
  };

  const applyPlanPaymentFilters = () => {
    setAppliedPlanPaymentFilters(planPaymentFilters);
    setActivePage(1);
    setFiltersOpen(false);
  };

  const resetAppliedPlanPaymentFilters = () => {
    setPlanPaymentFilters(emptyPlanPaymentFilters);
    setAppliedPlanPaymentFilters(emptyPlanPaymentFilters);
    setActivePage(1);
  };

  const activePlanPaymentFilterCount = Object.values(
    appliedPlanPaymentFilters,
  ).filter(Boolean).length;
  const hasPlanPaymentFilters = activePlanPaymentFilterCount > 0;
  const hasDraftPlanPaymentFilters =
    Object.values(planPaymentFilters).some(Boolean);

  const planPaymentFilterFields = (
    <>
      <label className={styles.filterField}>
        From
        <input
          type="date"
          className={styles.filterInput}
          value={planPaymentFilters.date_from}
          max={planPaymentFilters.date_to || today}
          onChange={(e) => handlePlanPaymentFilter("date_from", e.target.value)}
        />
      </label>
      <label className={styles.filterField}>
        To
        <input
          type="date"
          className={styles.filterInput}
          value={planPaymentFilters.date_to}
          min={planPaymentFilters.date_from || undefined}
          max={today}
          onChange={(e) => handlePlanPaymentFilter("date_to", e.target.value)}
        />
      </label>
      <label className={styles.filterField}>
        Payment Method
        <select
          className={styles.filterInput}
          value={planPaymentFilters.payment_method}
          onChange={(e) =>
            handlePlanPaymentFilter("payment_method", e.target.value)
          }
        >
          {planPaymentMethods.map((method) => (
            <option key={method.value} value={method.value}>
              {method.label}
            </option>
          ))}
        </select>
      </label>
    </>
  );

  const handleTabChange = (tab: string) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
    setActivePage(1);
  };

  return (
    <Box>
      {/* Bank Details Card */}
      <Box>
        <DetailsCard
          account={{ title: "Bank Name", value: "United Bank Limited" }}
          info={bankCredentials}
          loader={loader}
          details={bankDetails}
        ></DetailsCard>
      </Box>
      <Box>
        {/* All Approved Subscriptions  Table */}
        <Box className={styles.topLevelContainer}>
          <Box className={styles.topHeadContainer}>
            <Box>
              <div className={styles.tableHeading}>{headings.mainHeading}</div>
              <div className={styles.tableSubHeading}>
                {headings.subHeading}
              </div>
            </Box>
            <Box className={styles.filterContainer}>
              <div className={styles.gridContainer}>
                <Icons.SearchIcon className={styles.searchIcon} />
                <input
                  placeholder="Search"
                  className={styles.input}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </Box>
          </Box>
          {(isProductManager || isProjectHead) && (
            <Box style={{ backgroundColor: "#f2f7fb" }}>
              <Tabs
                value={paymentTabOrder.indexOf(activeTab)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ style: { backgroundColor: "#EF437B" } }}
              >
                <Tab
                  className={
                    activeTab === paymentTabs.all
                      ? classNames(styles.tabStyles, styles.activeLabel)
                      : styles.tabStyles
                  }
                  label="All Payments"
                  onClick={() => handleTabChange(paymentTabs.all)}
                />
                <Tab
                  className={
                    isApprovedTab
                      ? classNames(styles.tabStyles, styles.activeLabel)
                      : styles.tabStyles
                  }
                  label="Approved Payments"
                  onClick={() => handleTabChange(paymentTabs.approved)}
                />
                {/* In-app tabs hidden for now; re-add them to paymentTabOrder too.
                <Tab
                  className={
                    activeTab === paymentTabs.inApp
                      ? classNames(styles.tabStyles, styles.activeLabel)
                      : styles.tabStyles
                  }
                  label="In-App Payments"
                  onClick={() => handleTabChange(paymentTabs.inApp)}
                />
                <Tab
                  className={
                    isRefundsTab
                      ? classNames(styles.tabStyles, styles.activeLabel)
                      : styles.tabStyles
                  }
                  label="In-App Refunds"
                  onClick={() => handleTabChange(paymentTabs.inAppRefunds)}
                />
                */}
              </Tabs>
            </Box>
          )}
          {isApprovedTab && (
            <Box className={styles.planPaymentFilters}>
              <button
                className={styles.filtersToggle}
                onClick={openPlanPaymentFilters}
              >
                <Icons.FilterIcon />
                Filters
                {hasPlanPaymentFilters && ` (${activePlanPaymentFilterCount})`}
              </button>
              {hasPlanPaymentFilters && (
                <button
                  className={styles.clearFilters}
                  onClick={resetAppliedPlanPaymentFilters}
                >
                  Reset
                </button>
              )}
            </Box>
          )}
          <Drawer
            anchor="right"
            open={isApprovedTab && filtersOpen}
            onClose={() => setFiltersOpen(false)}
          >
            <Box className={styles.filterDrawer}>
              <div className={styles.filterDrawerHeader}>
                <span>Filters</span>
                <CloseIcon
                  className={styles.filterDrawerClose}
                  onClick={() => setFiltersOpen(false)}
                />
              </div>
              {planPaymentFilterFields}
              <div className={styles.filterDrawerActions}>
                <button
                  className={styles.clearFilters}
                  disabled={!hasDraftPlanPaymentFilters}
                  onClick={() => setPlanPaymentFilters(emptyPlanPaymentFilters)}
                >
                  Reset
                </button>
                <button
                  className={styles.filterApply}
                  onClick={applyPlanPaymentFilters}
                >
                  Apply
                </button>
              </div>
            </Box>
          </Drawer>
          <TableContainer
            className={styles.container}
            component={Paper}
            sx={
              screenWidth > 990
                ? {
                    width: `${screenWidth - 300}px`,
                  }
                : {
                    boxShadow: "none",
                  }
            }
          >
            <Table className={styles.table} aria-label="simple table">
              <TableHead>
                <TableRow className={styles.tableHead}>
                  {tableColumns?.map((heading, index) => {
                    return <TableCell key={index}>{heading.title}</TableCell>;
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {loader ? (
                  <TableSkeleton
                    rows={skeletonInfo.rows}
                    cellCount={
                      hasActions ? skeletonInfo.cellCount : tableColumns.length
                    }
                    actionCount={hasActions ? skeletonInfo.actions : 0}
                    showPersonData={false}
                  ></TableSkeleton>
                ) : isApprovedTab ? (
                  approvedSubscription?.map((item: any, index: number) => (
                    <TableRow
                      key={item?.id ?? index}
                      className={styles.tableRow}
                    >
                      <TableCell className={styles.transactionIdCell}>
                        <Tooltip
                          title={item?.transaction_id || "Not Provided"}
                          arrow
                        >
                          <span>{item?.transaction_id || "Not Provided"}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={styles.nameCell}>
                        <Tooltip
                          title={item?.user?.name || "Not Provided"}
                          arrow
                        >
                          <span>{item?.user?.name || "Not Provided"}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell className={styles.emailCell}>
                        <Tooltip
                          title={item?.user?.email || "Not Provided"}
                          arrow
                        >
                          <span>{item?.user?.email || "Not Provided"}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {item?.plan?.name || "Not Provided"}
                      </TableCell>
                      <TableCell>
                        {getCurrency(item?.plan)}
                        {item?.price ?? "Not Provided"}
                      </TableCell>
                      <TableCell>
                        {item?.plan?.level_label || "Not Provided"}
                      </TableCell>
                      <TableCell>{item?.coins ?? "Not Provided"}</TableCell>
                      <TableCell>
                        {item?.store_label ||
                          item?.payment_method_label ||
                          item?.payment_method ||
                          "Not Provided"}
                      </TableCell>
                      <TableCell>{formatDate(item?.paid_on)}</TableCell>
                    </TableRow>
                  ))
                ) : isRefundsTab ? (
                  approvedSubscription?.map((item: any, index: number) => (
                    <TableRow
                      key={item?.id ?? index}
                      className={styles.tableRow}
                    >
                      <TableCell className={styles.transactionIdCell}>
                        <Tooltip
                          title={item?.transaction_id || "Not Provided"}
                          arrow
                        >
                          <span>{item?.transaction_id || "Not Provided"}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {item?.user?.username || "Not Provided"}
                      </TableCell>
                      <TableCell className={styles.emailCell}>
                        <Tooltip
                          title={item?.user?.email || "Not Provided"}
                          arrow
                        >
                          <span>{item?.user?.email || "Not Provided"}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {item?.plan?.name || "Not Provided"}
                      </TableCell>
                      <TableCell>
                        {getCurrency(item?.plan)}
                        {item?.price ?? "Not Provided"}
                      </TableCell>
                      <TableCell>
                        {item?.plan?.level_label || "Not Provided"}
                      </TableCell>
                      <TableCell>{item?.coins ?? "Not Provided"}</TableCell>
                      <TableCell>{formatDate(item?.paid_on)}</TableCell>
                      <TableCell>{formatDate(item?.refunded_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  approvedSubscription?.map((item: any, index: number) => {
                    const {
                      user,
                      metadata,
                      price,
                      coins,
                      start_date,
                      transaction_type: transType,
                      status,
                      plan,
                    } = item;

                    const planName = metadata?.name;
                    const transactionType = transactionTypes?.find((type) => {
                      return type.id === transType;
                    });
                    const isSchool = user?.school ? true : false;
                    const transaction_status =
                      status === TRANSACTION_STATUSES.pending.value
                        ? "Pending"
                        : status === TRANSACTION_STATUSES.approved.value
                          ? "Approved"
                          : status === TRANSACTION_STATUSES.rejected.value
                            ? "Rejected"
                            : status === TRANSACTION_STATUSES.failed.value
                              ? "Failed"
                              : null;
                    const userFullName = user?.parent
                      ? user?.parent?.full_name
                      : user?.reader
                        ? user?.reader?.full_name
                        : user?.school?.full_name;

                    return (
                      <>
                        <TableRow key={index} className={styles.tableRow}>
                          <TableCell>
                            {userFullName ? userFullName : "Not Provided"}
                          </TableCell>
                          <TableCell>
                            {item?.plan?.name || "Not Provided"}
                          </TableCell>
                          <TableCell className={styles.emailCell}>
                            <Tooltip
                              title={user?.email || "Not Provided"}
                              arrow
                            >
                              <span>
                                {user?.email ? user?.email : "Not Provided"}
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {getCurrency(plan)}
                            {price ? price : "Not Provided"}{" "}
                          </TableCell>
                          <TableCell>
                            {plan?.level_label || "Not Provided"}
                          </TableCell>
                          <TableCell>
                            {coins ? coins : "Not Provided"}
                          </TableCell>
                          <TableCell>
                            {item?.transaction_method_label || "Not Provided"}
                          </TableCell>
                          <TableCell>
                            {start_date
                              ? moment(start_date).format("DD MMM YYYY")
                              : "Not Provided"}
                          </TableCell>
                          <TableCell className={styles.statusCell}>
                            <label className={styles.approved}>
                              {transaction_status
                                ? transaction_status
                                : "Not Provided"}
                            </label>
                          </TableCell>
                          <TableCell>
                            <div className={styles.actionContainer}>
                              <Tooltip
                                componentsProps={{
                                  tooltip: { className: "tooltip" },
                                  arrow: { className: "arrow" },
                                }}
                                placement="top"
                                arrow
                                title="View Receipt"
                              >
                                <div onClick={() => handleView(item)}>
                                  <Icons.EyeIcon2 />
                                </div>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      </>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box>
            {totalItems &&
            activePage &&
            totalPage &&
            totalPage > 1 &&
            approvedSubscription.length ? (
              <Pagination
                totalPages={totalPage}
                activePage={activePage}
                setActivePage={setActivePage}
              />
            ) : null}
          </Box>
          <Box>
            {totalItems &&
            activePage &&
            currentPage &&
            approvedSubscription.length ? (
              <ItemsPerPage
                setActivePage={setActivePage}
                currentPage={currentPage}
                setItemsPerPage={setItemsPerPage}
                itemsPerPage={itemsPerPage ? itemsPerPage : 10}
                totalItems={totalItems}
              />
            ) : null}
          </Box>
          <Box>
            {!loader && !(approvedSubscription?.length > 0) && <NoDataFound />}
          </Box>
        </Box>
      </Box>
      {/* View Transaction Details Modal */}
      <Box>
        <ViewTransactionListModal
          viewModal={viewUserTransactionList}
          handleViewClose={() => setViewUserTransactionList(false)}
          userId={subscriptionId}
          isSchool={isSchool}
        />
      </Box>
    </Box>
  );
};

export default Subscriptions;
